/**
 * Shell del panel de staff, visible tras un login exitoso de staff. A
 * diferencia del sitio público (mobile-first, tarjeta angosta), el panel es
 * una herramienta de back-office usada en una COMPUTADORA: es full-width
 * (`max-w-[1600px]`, usando ambos lados de la pantalla) con una BARRA LATERAL
 * IZQUIERDA de navegación en vez de pestañas superiores.
 *
 * IMPORTANTE — ancho real: el panel se monta FUERA del wrapper mobile
 * (`#publicShell`, `max-w-md sm:max-w-xl`) del index.html; de lo contrario ese
 * ancestro clampearía el `max-w-[1600px]` a ~576px (era la causa del "columna
 * angosta centrada" del intento anterior). Ver index.html (#view-admin es
 * hermano top-level de #publicShell) y js/main.js/handleAdmin (oculta
 * #publicShell mientras el panel está activo).
 *
 * Layout: en desktop (`lg`) la rail es persistente y estática y el contenido
 * se sienta a su derecha (`lg:flex`, `flex-1 min-w-0`). En mobile la rail es un
 * drawer (`fixed -translate-x-full`) que se abre con la hamburguesa
 * (`admin-sidebar-open`) y se cierra con el overlay o la X
 * (`admin-sidebar-close`) — mismo patrón que el drawer público de js/ui.js,
 * pero cableado localmente en js/admin.js.
 *
 * SECCIONES VISIBLES (staff-admin-access-split, Slice D1 — DOS paneles, no
 * uno): el panel STAFF (`/staff`) muestra exactamente `reservas`, `bloqueos`
 * (submenu/subsección propia de Reservas) y `fichaje` (ex-kiosco standalone
 * `/kiosco`, Slice A — AD9); el panel ADMIN (`/admin`) muestra
 * exactamente `empleados` (Control de Horarios y Liquidación de Sueldos),
 * `fichadas` (subsección de Empleados — Auditoría, mismo patrón que Bloqueos
 * bajo Reservas) y `liquidacion` (sección propia al mismo nivel que
 * Empleados — no una subsección, porque liquidar cruza empleados en batch,
 * no pertenece a uno solo). Ninguna sección aparece en los dos paneles (spec
 * `staff-panel-access-split` — "Panel Composition"). "Categorías" y
 * "Configuración" están OCULTAS por ahora (pendiente rediseño): su código
 * sigue montado en js/admin.js, solo se les quitó el item.
 * Los items de nav reutilizan las clases del menú público
 * (js/menu.js): botón `w-full p-4 rounded-2xl flex items-center gap-4`, activo
 * `bg-brand text-white shadow-lg`, inactivo `bg-surface text-text-muted`, con
 * un tile de ícono `w-10 h-10 rounded-xl`.
 *
 * Función de render pura: devuelve HTML sin estado ni efectos — el content
 * mount (`#admin-content-mount`) arranca vacío, `js/admin.js` lo puebla tras
 * montar el shell y marca visualmente la sección activa.
 *
 * @param {'reservas'|'bloqueos'|'fichaje'|'faq'|'reportes'|'giftcards'|'empleados'|'fichadas'|'liquidacion'|'faq-admin'|'faq-busquedas'|'reportes-admin'|'giftcards-admin'} [activeTab='reservas'] — sección activa inicial.
 * @param {'staff'|'admin'} [panel='staff'] — panel activo (D1): decide qué juego de secciones y qué título se renderiza.
 * @returns {string} HTML del shell del panel.
 */

/** Secciones del panel STAFF (`/staff`) — Reservas, Bloqueos, Fichaje, Preguntas, Reportes, Gift cards. */
const SECTIONS_STAFF = [
    { tab: 'reservas', label: 'Reservas', icon: 'fa-calendar-check' },
    // Bloqueos = subsección de Reservas (submenu indentado, sección propia).
    { tab: 'bloqueos', label: 'Bloqueos', icon: 'fa-ban', sub: true },
    // Fichaje (staff-admin-access-split, Slice A): ex-kiosco standalone
    // `/kiosco`, ahora pestaña propia del panel de staff (AD9). No es
    // subsección de nada — sección de nivel propio, como Reservas.
    { tab: 'fichaje', label: 'Fichaje', icon: 'fa-clock' },
    // Preguntas frecuentes (staff-faq): lectura; se edita en el panel admin.
    { tab: 'faq', label: 'Preguntas', icon: 'fa-circle-question' },
    // Reportes (staff-reportes): mensaje firmado con nombre + PIN para Caro.
    { tab: 'reportes', label: 'Reportes', icon: 'fa-comment-dots' },
    // Gift cards (staff-giftcards): emitir y canjear en el mostrador.
    { tab: 'giftcards', label: 'Gift cards', icon: 'fa-gift' },
];

/** Secciones del panel ADMIN (`/admin`) — Empleados, Fichadas, Liquidación, FAQ, Búsquedas, Reportes, Gift cards. */
const SECTIONS_ADMIN = [
    // Control de Horarios y Liquidación de Sueldos (slice 1: Empleados).
    { tab: 'empleados', label: 'Empleados', icon: 'fa-users' },
    // Fichadas = subsección de Empleados (slice 3 — Auditoría), mismo patrón
    // que Bloqueos bajo Reservas.
    { tab: 'fichadas', label: 'Fichadas', icon: 'fa-clipboard-user', sub: true },
    // Liquidación (slice 5) — sección propia, NO subsección: liquidar corre
    // en batch sobre varios empleados a la vez, no cuelga de uno solo.
    { tab: 'liquidacion', label: 'Liquidación', icon: 'fa-money-check-dollar' },
    // FAQ (staff-faq): editor de lo que el staff lee en Preguntas, y Búsquedas
    // como subsección (mismo patrón que Fichadas bajo Empleados).
    { tab: 'faq-admin', label: 'FAQ', icon: 'fa-book-open' },
    { tab: 'faq-busquedas', label: 'Búsquedas', icon: 'fa-magnifying-glass', sub: true },
    // Reportes (staff-reportes): los mensajes del staff; `badge` = contador
    // de Nuevos que pinta `refreshReportesBadge` (js/reportes.js).
    { tab: 'reportes-admin', label: 'Reportes', icon: 'fa-inbox', badge: true },
    // Gift cards (staff-giftcards): estados, totales y anular.
    { tab: 'giftcards-admin', label: 'Gift cards', icon: 'fa-gift' },
    // "Categorías" y "Configuración" ocultas por ahora (pendiente rediseño):
    // su código sigue montado en js/admin.js, solo se les quitó el item de nav.
];

/**
 * Mount del banner de jornadas abiertas hace más de 12 h (F-B, owner decision
 * 2026-09-27). SOLO en el panel admin: el panel staff nunca lo tiene, así
 * que un `counter` jamás dispara esa lectura. Arranca oculto; `js/admin.js`
 * lo puebla (y lo vuelve a ocultar si no hay nada o si la lectura falla).
 */
const JORNADAS_BANNER_MOUNT = '<div id="admin-jornadas-abiertas-banner" hidden></div>';

/** Título de la rail/header mobile por panel (D1 — "Panel Composition"). */
const PANEL_TITLES = { staff: 'Panel Staff', admin: 'Panel Admin' };

/**
 * Clases de un item de nav activo / inactivo. `js/admin.js` (updateTabButtons)
 * las alterna al cambiar de sección — por eso son listas: TODAS se togglean.
 */
export const TAB_ACTIVE_CLASSES = ['bg-brand', 'text-white', 'shadow-lg'];
export const TAB_INACTIVE_CLASSES = ['bg-surface', 'text-text-muted'];

/** Base compartida por cada item de nav (el padding/gap se agregan por item — más chicos para los sub). */
const NAV_ITEM_BASE = 'w-full rounded-2xl flex items-center transition-all';

/** Tile del ícono: neutral pero legible en ambos estados (el ícono hereda currentColor). */
const NAV_ICON_TILE =
    'w-10 h-10 rounded-xl flex items-center justify-center bg-black/5 dark:bg-white/10 shrink-0';

/** Tile más chico para los items secundarios (sub) — un sub debe leerse más liviano que el padre. */
const NAV_ICON_TILE_SUB =
    'w-7 h-7 rounded-lg flex items-center justify-center bg-black/5 dark:bg-white/10 shrink-0';

/**
 * @param {'reservas'|'bloqueos'|'fichaje'|'faq'|'reportes'|'giftcards'|'empleados'|'fichadas'|'liquidacion'|'faq-admin'|'faq-busquedas'|'reportes-admin'|'giftcards-admin'} activeTab
 * @param {'staff'|'admin'} panel
 * @returns {string}
 */
function renderNavItems(activeTab, panel) {
    const sections = panel === 'admin' ? SECTIONS_ADMIN : SECTIONS_STAFF;
    return sections.map(({ tab, label, icon, sub, badge }) => {
        const isActive = tab === activeTab;
        const stateClasses = (isActive ? TAB_ACTIVE_CLASSES : TAB_INACTIVE_CLASSES).join(' ');
        // Los sub-items (Bloqueos) van más compactos e indentados: leen como
        // secundarios. La sangría se hace con PADDING-LEFT, no con `ml-6`: con
        // `w-full` un margin izquierdo empujaba la caja y su borde derecho
        // sobresalía ~1.5rem respecto de "Reservas". Con pl/pr el botón queda
        // alineado (mismo borde derecho) y el contenido se ve indentado igual.
        const sizing = sub ? 'pl-8 pr-4 py-2 gap-2' : 'p-4 gap-4';
        const tileClasses = sub ? NAV_ICON_TILE_SUB : NAV_ICON_TILE;
        const iconSize = sub ? 'text-sm' : 'text-lg';
        const labelSize = sub ? 'text-[11px]' : 'text-xs';
        return `
            <button type="button" role="tab" data-action="admin-tab" data-tab="${tab}"
                aria-selected="${isActive}"
                class="${NAV_ITEM_BASE} ${sizing} ${stateClasses}${badge ? ' group' : ''}">
                <span class="${tileClasses}">
                    <i class="fa-solid ${icon} ${iconSize}" aria-hidden="true"></i>
                </span>
                <span class="${labelSize} font-black uppercase tracking-tight">${label}</span>
                ${badge ? `<span data-tab-badge="${tab}" hidden class="ml-auto min-w-6 h-6 px-2 rounded-full bg-brand text-white group-aria-selected:bg-white group-aria-selected:text-brand text-[11px] leading-6 text-center font-black tabular-nums"></span>` : ''}
            </button>
        `;
    }).join('');
}

export const AdminShell = (activeTab = 'reservas', panel = 'staff') => `
    <div class="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Barra superior mobile: hamburguesa + título. La rail se colapsa en drawer bajo lg. -->
        <div class="flex items-center gap-3 py-4 lg:hidden">
            <button type="button" data-action="admin-sidebar-open" aria-label="Abrir menú del panel"
                aria-controls="admin-sidebar"
                class="w-11 h-11 rounded-xl border border-border-subtle bg-surface text-text-primary flex items-center justify-center">
                <i class="fa-solid fa-bars-staggered text-lg" aria-hidden="true"></i>
            </button>
            <h2 class="text-lg font-black uppercase tracking-tight text-text-primary">${PANEL_TITLES[panel] ?? PANEL_TITLES.staff}</h2>
        </div>

        <div class="lg:flex lg:gap-8 lg:py-8">
            <!-- Overlay del drawer (solo mobile) -->
            <div data-action="admin-sidebar-close" id="admin-sidebar-overlay"
                class="hidden fixed inset-0 z-30 bg-black/40 lg:hidden"></div>

            <!-- Rail lateral: drawer en mobile, columna persistente en desktop -->
            <aside id="admin-sidebar"
                class="fixed inset-y-0 left-0 z-40 w-64 bg-base border-r border-border-subtle p-6 flex flex-col -translate-x-full transition-transform duration-300 lg:translate-x-0 lg:sticky lg:top-8 lg:self-start lg:h-[calc(100vh-4rem)] lg:inset-auto lg:z-auto lg:w-64 lg:shrink-0 lg:border-r-0 lg:bg-transparent lg:p-0">
                <div class="flex items-center justify-between mb-8 lg:mb-8">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center shrink-0">
                            <i class="fa-solid fa-shield-halved text-brand" aria-hidden="true"></i>
                        </div>
                        <div class="leading-tight">
                            <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">Panache</span>
                            <span class="block text-sm font-black uppercase tracking-tight text-text-primary">${PANEL_TITLES[panel] ?? PANEL_TITLES.staff}</span>
                        </div>
                    </div>
                    <button type="button" data-action="admin-sidebar-close" aria-label="Cerrar menú del panel"
                        class="lg:hidden p-2 text-text-muted hover:text-text-primary">
                        <i class="fa-solid fa-xmark text-xl" aria-hidden="true"></i>
                    </button>
                </div>

                <nav role="tablist" aria-label="Secciones del panel" class="flex flex-col gap-3">
                    ${renderNavItems(activeTab, panel)}
                </nav>

                <button data-action="admin-logout" aria-label="Cerrar sesión"
                    class="mt-auto w-full p-4 rounded-2xl flex items-center gap-4 bg-surface text-text-muted hover:text-brand transition-colors">
                    <span class="${NAV_ICON_TILE}">
                        <i class="fa-solid fa-right-from-bracket text-lg" aria-hidden="true"></i>
                    </span>
                    <span class="text-xs font-black uppercase tracking-tight">Cerrar sesión</span>
                </button>
            </aside>

            <!-- Contenido de la sección activa -->
            <div class="flex-1 min-w-0 pb-16 pt-2 lg:pt-0">
                ${panel === 'admin' ? JORNADAS_BANNER_MOUNT : ''}
                <div id="admin-content-mount"></div>
            </div>
        </div>
    </div>
`;
